/**
 * Learning Paths Client - Client-side wrapper for learning path page
 * Fetches and provides user progress data to child components
 */
'use client';
import LearningPathsHero from './learning-paths-hero';
import LearningPathContent from './LearningPathContent';
import { CMSLearningPath } from '@/types/cms-lp';
import { useLearningPathCourseProgress } from '@/hooks/useUserLearningPaths';
import { usePartnerOverlay } from '@/hooks/usePartnerOverlay';
import { useLoginModals } from '@/components/Login/common/LoginModalsManager';
import BlurOverlay, { PartnerOnlyMessage } from '@/components/common/blur-overlay';

/** Derive whether this learning path is partner-only from the LP entry or any of its courses */
function isPartnerOnlyLearningPath(data: CMSLearningPath | null | undefined): boolean {
	if (!data) return false;
	if (data.is_partner_specific === true) return true;
	const courses = data.courses?.course;
	if (Array.isArray(courses) && courses.some((c: { is_partner_specific?: boolean }) => c?.is_partner_specific === true)) {
		return true;
	}
	return false;
}

export default function LearningPathsClient({ learningPath, learningPathsWaysData }: { learningPath: string, learningPathsWaysData: CMSLearningPath }) {
	const { 
		data: coursesWithProgress,
		learningPathProgress,
	} = useLearningPathCourseProgress(
		learningPathsWaysData.uid,
		learningPathsWaysData.courses
	);

	const isPartnerContent = isPartnerOnlyLearningPath(learningPathsWaysData);
	const { overlayState: showPartnerOverlay, handleLogout } = usePartnerOverlay({
		isPartnerContent,
	});
	const { openLoginModalWithPartnerRole } = useLoginModals();

	// Show overlay if content is partner-only and user is not a partner
	if (showPartnerOverlay.show) {
		return (
			<BlurOverlay
				open={showPartnerOverlay.show}
				message={<PartnerOnlyMessage />}
				buttons={[
					{
						id: 'partner-login',
						title: 'Log In as Partner',
						variant: 'primary',
						onClick: async () => {
							await handleLogout();
							openLoginModalWithPartnerRole();
						},
					},
					{
						id: 'academy-home',
						title: 'Go to Academy Home',
						variant: 'secondary',
						redirectPath: '/academy',
					},
				]}
			/>
		);
	}

	return (
		<>
			<div className="bg-amethyst-opaque-accent-2">
			<div className="section h-full pt-7! pb-7!">
				<LearningPathsHero
					learningPathsWaysData={learningPathsWaysData}
					userLpData={learningPathProgress ?? null}
					markdownLearningPathSlug={learningPath}
				/>
			</div>
			</div>

			<div className="section">
				<LearningPathContent
					learningPath={learningPath}
					learningPathData={learningPathsWaysData}
					courses={coursesWithProgress}  // Pass the courses array
					userLpData={learningPathProgress}  // Pass the overall progress
				/>
			</div>
		</>
	)
}